import { useEffect } from 'react';
import { useErrorStore, getActionSuggestion } from '@/stores/errorStore';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { AlertTriangle, Copy, X, RefreshCw } from "lucide-react";
import { toast } from "sonner";

/**
 * Dialog globale per mostrare dettagli errore.
 * Montare una sola volta in App.jsx
 */
export default function ErrorDialog() {
  const { lastError, showErrorDialog, closeErrorDialog } = useErrorStore();

  const handleCopyError = () => {
    if (!lastError) return;
    
    const errorText = `
=== ERRORE ${lastError.code} ===
Tipo: ${lastError.type}
Titolo: ${lastError.title}
Messaggio: ${lastError.message}
Contesto: ${lastError.context || 'N/A'}
Timestamp: ${lastError.timestamp}

--- Dettagli Tecnici ---
${JSON.stringify(lastError.technical, null, 2)}
`.trim();
    
    navigator.clipboard.writeText(errorText);
    toast.success("Dettagli errore copiati negli appunti");
  };

  if (!lastError) return null;

  const suggestion = getActionSuggestion(lastError.type, lastError.code);

  return (
    <Dialog open={showErrorDialog} onOpenChange={(open) => !open && closeErrorDialog()}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <div className="flex items-center gap-3">
            <div className="p-2 bg-red-100 rounded-full">
              <AlertTriangle className="w-6 h-6 text-red-600" />
            </div>
            <div>
              <DialogTitle className="text-lg text-red-700">
                {lastError.title}
              </DialogTitle>
              <Badge variant="outline" className="mt-1 text-xs font-mono">
                {lastError.code}
              </Badge>
            </div>
          </div>
        </DialogHeader>
        
        <div className="space-y-4 py-2">
          {/* Messaggio principale */}
          <div className="bg-slate-50 p-3 rounded-lg">
            <p className="text-sm text-slate-700">{lastError.message}</p>
          </div>
          
          {/* Suggerimento */}
          <div className="flex items-start gap-2 text-sm text-slate-600">
            <RefreshCw className="w-4 h-4 mt-0.5 text-blue-500" />
            <span>{suggestion}</span>
          </div>
          
          {/* Contesto */}
          {lastError.context && (
            <div className="text-xs text-slate-500">
              <span className="font-medium">Operazione:</span> {lastError.context}
            </div>
          )}
          
          {/* Dettagli tecnici (collapsible) */}
          <details className="text-xs">
            <summary className="cursor-pointer text-slate-500 hover:text-slate-700">
              Mostra dettagli tecnici
            </summary>
            <pre className="mt-2 p-2 bg-slate-900 text-slate-100 rounded overflow-auto max-h-40 text-xs">
              {JSON.stringify(lastError.technical, null, 2)}
            </pre>
          </details>
          
          {/* Timestamp */}
          <div className="text-xs text-slate-400 text-right">
            {new Date(lastError.timestamp).toLocaleString('it-IT')}
          </div>
        </div>

        <DialogFooter className="gap-2">
          <Button variant="outline" size="sm" onClick={handleCopyError}>
            <Copy className="w-4 h-4 mr-1" />
            Copia dettagli
          </Button>
          <Button size="sm" onClick={closeErrorDialog}>
            <X className="w-4 h-4 mr-1" />
            Chiudi
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
